import Alert from 'component/base/Alert';
import language from 'config/language';
import { Design, IconName, Intent } from 'enums';
import { TestSelector } from 'enums/tests';
import usePopup from 'hooks/browserHooks/usePopup';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { v1 as uuid } from 'uuid';

import MicrosoftButton from './MicrosoftButton';

const realm = process.env.KEYCLOAK_REALM;

type OAuthFailureData = {
  error: string;
  errorDescription: string;
};

type OAuthSuccessData = {
  code: string;
  state: string;
};

interface Props {
  authType: 'signin' | 'signup';
  onAuthSuccess: (code: string) => Promise<any>;
  onFlowEnd?: VoidFunction;
  onAuthFail?: (data: OAuthFailureData | OAuthSuccessData) => VoidFunction;
  onFlowStart?: VoidFunction;
  redirectRoute?: string;
}

/**
 * Renders a signin/up with microsoft button and handles the oauth logic through
 * microsoft authentication and redirect from keycloak with code.  Renders spinner
 * while auth flow is ongoing.
 * @param props
 * @property props.authType - whether using component in the signin or signup flows.
 * @property props.onAuthSuccess - async function that will be called on successful redirect
 *   by keycloak.  Will be passed the 'code' url param provided by keycloak.
 * @property props.onFlowEnd - is called when the user closes the popup auth window
 *   without completing auth, or after props.onAuthSuccess onAuthFail if auth is completed.
 * @property props.onAuthFail - is called if redirect completes with a code url param passed
 *   by keycloak.
 * @property props.onFlowStart - is called when user clicks the ms button and the popup is opened.
 * @property props.redirectRoute - the path where the keycloak will redirect after ms auth is complete.
 *   Router must render a flow completion handler - which calls window.oauth.notifySuccess /
 *   notifyfailure.  See MicrosoftComplete.tsx.
 */
const MicrosoftAuthentication = ({
  authType,
  onAuthSuccess,
  onFlowEnd,
  onAuthFail,
  onFlowStart,
  redirectRoute,
}: Props): JSX.Element => {
  const [uid] = useState(uuid());
  const [oauthErrorMsg, setOauthErrorMsg] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const buttonIcon =
    authType === 'signin'
      ? IconName.MICROSOFT_SIGNIN_DARK
      : IconName.MICROSOFT_SIGNUP_DARK;

  let iconDataTest =
    authType === 'signin'
      ? TestSelector.UI_ICON_MICROSOFT_SIGNIN
      : TestSelector.UI_ICON_MICROSOFT_SIGNUP;
  if (showSpinner) {
    iconDataTest = TestSelector.UI_ICON_LOADER;
  }

  const url = `https://auth.beecastle.com/auth/realms/${realm}/protocol/openid-connect/auth?response_type=code&client_id=bc-fe&redirect_uri=https://${window.location.host}${redirectRoute}&state=${uid}&kc_idp_hint=microsoft&prompt=login`;
  const title = 'Microsoft Sign In';

  const onOpenPopup = useCallback(() => {
    setShowSpinner(true);
    if (typeof onFlowStart === 'function') {
      onFlowStart();
    }
  }, [onFlowStart, setShowSpinner]);

  const onClosePopup = useCallback(() => {
    setShowSpinner(false);
    if (typeof onFlowEnd === 'function') {
      onFlowEnd();
    }
  }, [onFlowEnd, setShowSpinner]);

  const { cancelOnClose, clearPopupInterval, createPopup } = usePopup(
    url,
    title,
    onClosePopup,
    onOpenPopup,
  );

  useEffect(() => {
    // Callback for success
    const notifySuccess = async (data: OAuthSuccessData) => {
      // This keeps the spinner while the auth flow continues
      cancelOnClose();
      if (data && data.state === uid && data.code) {
        await onAuthSuccess(data.code);
      } else if (typeof onAuthFail === 'function') {
        onAuthFail(data);
      }
      if (typeof onFlowEnd === 'function') {
        onFlowEnd();
      }
      /*
        Now do these manually because we cancelled the popup onClose
        handler.

        TODO: in most flows - a redirect happens immediately after
        onAuthSuccess completes, leading to a setState after mount
        error.  Split redirect call outside of onAuthSuccess
        and call after these two.
      */
      setShowSpinner(false);
      clearPopupInterval();
    };

    // Callback for failure
    const notifyFailure = (data: OAuthFailureData) => {
      const { errorDescription } = data;
      if (errorDescription) {
        setOauthErrorMsg(errorDescription);
      } else {
        setOauthErrorMsg(language.auth.microsoft_signup.errors.general_error);
      }
      if (typeof onAuthFail === 'function') {
        onAuthFail(data);
      }
      if (typeof onFlowEnd === 'function') {
        onFlowEnd();
      }
    };

    // Add callback score to window so that popup can callback
    window.oauth = {
      notifySuccess,
      notifyFailure,
    };
  }, [
    cancelOnClose,
    clearPopupInterval,
    onAuthFail,
    onAuthSuccess,
    onFlowEnd,
    onFlowStart,
    setShowSpinner,
    uid,
  ]);

  return (
    <>
      {oauthErrorMsg && <Alert>{oauthErrorMsg}</Alert>}
      <MicrosoftButton
        onClick={!showSpinner ? createPopup : undefined}
        intent={Intent.PRIMARY}
        block
        design={Design.LARGE}
        iconName={!showSpinner ? buttonIcon : IconName.LOADER}
        // Used to locate in e2e tests
        data-test={TestSelector.AUTHENTICATION_MICROSOFT_BUTTON}
        iconThemeColor="white"
        rotate={showSpinner}
      />
    </>
  );
};

MicrosoftAuthentication.defaultProps = {
  onAuthFail: null,
  onFlowEnd: null,
  onFlowStart: null,
  redirectRoute: '/oauth/microsoft/complete',
};

export default memo(MicrosoftAuthentication);
